import React, { useContext } from "react";
import { CoreContext } from "../../../../context/global/CoreContext";
import {
  getLanguageOption,
  SHIFT_STATUS_COLORS,
  UI_COPY,
  clockInStageText,
  clockInFieldLabel,
} from "../../../../config";
import { useUiText } from "../../../../hooks/useUiText";
import { useWorkDescriptionTranslation } from "../useWorkDescriptionTranslation";

import {
  decimalHoursToHHMM,
  convert24To12String,
  removeLeadingZeros,
  decimalToHoursMinutes,
} from "../../../../utils/datetime";
import { renderLinkifiedContent } from "../../../../utils/dom";

import CancelClockIn from "../components/CancelClockIn";

import {
  CheckCircle,
  ExclamationCircle,
  Calendar,
  MinusCircle,
} from "../../../../components/icons/svg/outline/index";
import { IoMdStopwatch } from "react-icons/io";

/**
 * Insert `workDescriptionTranslation` immediately after `workDescription` for display only.
 *
 * @param {Object} shift Display copy of a shift row.
 * @param {string} translatedText Translation of `workDescription` into the user's language.
 * @returns {Object} Shift copy with the extra key when translation is present.
 */
function withWorkDescriptionTranslation(shift, translatedText) {
  if (!translatedText) {
    return shift;
  }
  const next = {};
  Object.entries(shift).forEach(([key, value]) => {
    next[key] = value;
    if (key === "workDescription") {
      next.workDescriptionTranslation = translatedText;
    }
  });
  return next;
}

/**
 * ShiftSummary Component
 *
 * Displays current shift information for a user with dynamic content based on shift status.
 *
 * Features:
 * - Shows different UI for "Clocked In" vs "Complete Shift" states
 * - Displays job information with friendly names resolved from IDs
 * - Handles time formatting and overtime calculations
 * - Provides visual indicators for shift status (clock icon or check mark)
 * - Supports animated transitions between states
 *
 * Data Processing:
 * - Converts job and work scope IDs to human-readable names
 * - Automatically calculates and formats regular and overtime hours
 * - Formats decimal hours to HH:MM display format
 * - Handles overtime calculation (hours > 8 are considered overtime)
 *
 * Context Usage:
 * - CoreContext: Uses jobsList to resolve job and work scope names
 * - TimekeepingContext: Uses shiftData for all shift details and showClockOutContent for animations
 *
 * @param {Object} props Component props.
 * @param {Object} props.shift Shift row to display.
 * @param {"card"|string} [props.layout="card"] Layout variant.
 * @param {string} [props.className] Extra wrapper classes.
 * @param {boolean} [props.animate=true] Whether status transitions animate.
 * @param {Array<string>} [props.removeFields=[]] Field keys to hide.
 * @returns {JSX.Element} A UI card showing the current shift information with appropriate styling
 */
const ShiftSummary = ({
  shift,
  layout = "card",
  className,
  animate = true,
  removeFields = [],
}) => {
  const { jobsList, scopesList } = useContext(CoreContext);
  const { language, t } = useUiText();
  const { translatedText, detectedSourceLanguage } =
    useWorkDescriptionTranslation(shift?.workDescription);
  const previewText =
    detectedSourceLanguage === language ? "" : translatedText;

  const shiftStatus = shift?.stage || "No Shift Data"; // Default to "No Shift Data" if stage is not available

  const styling = SHIFT_STATUS_COLORS;

  const layoutStyling = {
    card: "rounded-b-md",
    block: "",
  };

  const icon = {
    "Clocked In": <IoMdStopwatch className="h-7 w-7" />,
    "Current Shift": <Calendar />,
    "Complete Shift": <CheckCircle />,
    "Shift (Draft)": <Calendar />,
    Shift: <Calendar />,
    "Missed Shift": <ExclamationCircle />,
    "No Shift": <MinusCircle />,
    "No Shift Data": null,
  };

  // Update property access to match the properties set in TimekeepingState.jsx
  const jobObj = jobsList.find((j) => j.id === Number(shift?.job));
  const jobName = jobObj
    ? `${jobObj.number} - ${jobObj.description}`
    : shift?.job || t(UI_COPY.clockIn.unknownJob); // Format hours display and handle overtime calculation
  // Handle overtime calculations and display
  const shiftCopy = withWorkDescriptionTranslation(
    { ...shift },
    previewText
  );

  if (Number(shiftCopy?.totalHours) > 8) {
    // Store decimal values for calculations
    const regularDecimal = 8;
    const overtimeDecimal = Number(shiftCopy?.totalHours) - 8.0;

    // Format as HH:MM for display
    shiftCopy.regularHours = decimalHoursToHHMM(regularDecimal);
    shiftCopy.overtimeHours = decimalHoursToHHMM(overtimeDecimal);
  } else if (shiftCopy?.totalHours) {
    // If less than or equal to 8 hours, just convert the total hours to HH:MM
    shiftCopy.totalHours = decimalHoursToHHMM(shiftCopy.totalHours);
  }

  const scopeObj = scopesList?.find((s) => s.id === Number(shift?.workScope));
  const scopeName = scopeObj?.title || shift?.workScope || t(UI_COPY.clockIn.unknownScope);

  // Finally, remove any unwanted data fields from the shift object
  const unwantedFields = {
    "Missed Shift": ["id", "stage", "date", "day"],
    "Current Shift": ["id", "stage", "date", "day"],
    "Complete Shift": ["id", "stage", "date", "day"],
    "Clocked In": ["id", "stage", "date", "day"],
    "No Shift": ["id", "stage", "date", "day"],
    "Shift (Draft)": ["id", "stage", "date", "day"],
  };

  // Add the passed in removeFields from the unwantedFields
  removeFields.forEach((field) => {
    unwantedFields[shiftCopy.stage] = [
      ...(unwantedFields[shiftCopy.stage] || []),
      field,
    ];
  });

  return (
    shift && (
      <div
        className={`SHIFT-SUMMARY ${className} ${styling[shiftStatus]} overflow-clip p-2 px-4 ${layoutStyling[layout]} h-full min-h-full transition-all duration-700 ease-in-out`}
      >
        <div className={`transition-all duration-700 ease-in`}>
          <div className="flex flex-row items-start space-x-6">
            <div
              className={`flex items-center space-x-2 mr-auto ${shift?.stage === "Complete Shift" && animate ? "animate-pulse-scale text-emerald-800" : ""}`}
            >
              <div
                className={`flex-shrink-0 ${shift?.stage === "Complete Shift" && animate ? "animate-spin-once" : ""}`}
              >
                {shift?.stage && icon[shiftStatus]}
              </div>
              <span className="font-semibold">
                {clockInStageText(shift?.stage, language)}
              </span>
            </div>
          </div>
          {shift?.stage === "Shift (Draft)" && (
            <div className="text-sm text-gray-500 mt-2">
              <i>{t(UI_COPY.clockIn.draftNote)}</i>
            </div>
          )}
          <div className="flex flex-col">
            {shiftCopy &&
              Object.keys(shiftCopy)
                .filter(
                  (key) => !unwantedFields[shiftCopy.stage]?.includes(key)
                )
                .map((key) => (
                  <div className="mt-1 text-sm" key={key}>
                    <div className="text-gray-700">
                      <b>
                        {key === "workDescriptionTranslation"
                          ? getLanguageOption(language)?.label
                          : clockInFieldLabel(key, language)}
                        :
                      </b>{" "}
                      {key === "job" ? (
                        jobName
                      ) : key === "workScope" ? (
                        scopeName
                      ) : key === "workDescription" ||
                        key === "workDescriptionTranslation" ||
                        key === "notes" ? (
                        <div className="inline">
                          {renderLinkifiedContent(shiftCopy[key])}
                        </div>
                      ) : key === "startTime" || key === "finishTime" ? (
                        convert24To12String(removeLeadingZeros(shiftCopy[key]))
                      ) : key === "regularTime" || key === "overtime" || key === "doubleTime" ? (
                        decimalToHoursMinutes(
                          removeLeadingZeros(shiftCopy[key])
                        )
                      ) : key === "breaksCompleted" ? (
                        shiftCopy[key] ? (
                          t(UI_COPY.clockIn.yes)
                        ) : (
                          t(UI_COPY.clockIn.no)
                        )
                      ) : (
                        shiftCopy[key]
                      )}
                    </div>
                  </div>
                ))}
          </div>
          {shift?.stage === "Clocked In" && (
            <CancelClockIn className={"mt-4 opacity-75 hover:opacity-100"} />
          )}
        </div>
      </div>
    )
  );
};

export default ShiftSummary;
